/**
 * RoyaltyDashboard - Auto-populated royalty data from transactions with date filter
 */

import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { Badge } from '../../ui/components/Badge';
import { useRoyaltySummary, useRoyaltySales } from '../../hooks/useDistribution';

interface RoyaltyDashboardProps {
  projectSlug: string;
}

export const RoyaltyDashboard: React.FC<RoyaltyDashboardProps> = ({ projectSlug }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [appliedDates, setAppliedDates] = useState<{ start_date?: string; end_date?: string }>({});

  const { data: summaryData, isLoading: summaryLoading } = useRoyaltySummary(
    projectSlug,
    appliedDates.start_date || appliedDates.end_date ? appliedDates : undefined
  );
  const { data: salesData, isLoading: salesLoading } = useRoyaltySales(
    projectSlug,
    { limit: 25, ...appliedDates }
  );

  const handleFilter = () => {
    setAppliedDates({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    });
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setAppliedDates({});
  };

  const sales = salesData?.sales || [];

  return (
    <div className="flex flex-col gap-4">
      <span className="text-body-bold font-body-bold text-default-font">Royalty Dashboard</span>

      {/* Date filter */}
      <div className="flex items-end gap-3">
        <TextField className="h-auto w-40" label="Start Date">
          <TextField.Input type="date" value={startDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)} />
        </TextField>
        <TextField className="h-auto w-40" label="End Date">
          <TextField.Input type="date" value={endDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)} />
        </TextField>
        <Button variant="neutral-secondary" size="small" onClick={handleFilter}>Filter</Button>
        {(appliedDates.start_date || appliedDates.end_date) && (
          <Button variant="neutral-tertiary" size="small" onClick={handleClearFilter}>Clear</Button>
        )}
      </div>

      {/* Summary cards */}
      {summaryLoading ? (
        <div className="text-subtext-color py-4">Loading royalty data...</div>
      ) : summaryData?.summary ? (
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded border border-solid border-neutral-border p-4">
            <span className="text-caption font-caption text-subtext-color block">Total Royalties</span>
            <span className="text-heading-3 font-heading-3 text-default-font">
              {summaryData.summary.total_royalty_xrp.toLocaleString(undefined, { maximumFractionDigits: 2 })} XRP
            </span>
          </div>
          <div className="rounded border border-solid border-neutral-border p-4">
            <span className="text-caption font-caption text-subtext-color block">Sales with Royalties</span>
            <span className="text-heading-3 font-heading-3 text-default-font">
              {summaryData.summary.sale_count.toLocaleString()}
            </span>
          </div>
          <div className="rounded border border-solid border-neutral-border p-4">
            <span className="text-caption font-caption text-subtext-color block">Avg per Sale</span>
            <span className="text-heading-3 font-heading-3 text-default-font">
              {summaryData.summary.sale_count > 0
                ? (summaryData.summary.total_royalty_xrp / summaryData.summary.sale_count).toLocaleString(undefined, { maximumFractionDigits: 4 })
                : '0'} XRP
            </span>
          </div>
        </div>
      ) : null}

      {/* Sales list */}
      <span className="text-body-bold font-body-bold text-default-font mt-2">Recent Sales</span>

      {salesLoading ? (
        <div className="text-subtext-color py-4">Loading sales...</div>
      ) : sales.length === 0 ? (
        <div className="text-subtext-color py-4">No royalty sales found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-body font-body">
            <thead>
              <tr className="border-b border-neutral-border text-left text-caption font-caption text-subtext-color">
                <th className="px-2 py-1">Date</th>
                <th className="px-2 py-1">NFT</th>
                <th className="px-2 py-1 text-right">Price (XRP)</th>
                <th className="px-2 py-1 text-right">Royalty (XRP)</th>
                <th className="px-2 py-1">Est.</th>
              </tr>
            </thead>
            <tbody>
              {sales.map(sale => (
                <tr key={sale.id} className="border-b border-neutral-border">
                  <td className="px-2 py-1 text-default-font">
                    {new Date(sale.transaction_date).toLocaleDateString()}
                  </td>
                  <td className="px-2 py-1 text-default-font">
                    {sale.nft_name || sale.nft_token_id.slice(0, 12) + '...'}
                  </td>
                  <td className="px-2 py-1 text-right text-default-font">
                    {(sale.price_in_drops / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-2 py-1 text-right text-default-font">
                    {(sale.royalty_amount_drops / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  </td>
                  <td className="px-2 py-1">
                    {sale.royalty_is_estimated && <Badge variant="warning">Est</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
